import React, { useContext, useMemo } from 'react'
import { UseFormReturn } from 'react-hook-form'
import i18next from 'i18next'
import { FormInputsType } from '@microservices/wiskey-react-components/dist/types'

import { ACTION_CODE, EVENT_CODE_GANTT, GENERATOR_INPUT_TYPE, REGEX } from '@constants'
import { SelectOption } from '@types'

import {
  GANTT_BIND_TYPE_LIST,
  GanttActionType,
  PageContext,
} from '@gantt/components/GanttCreateOrEdit'
import { EMPTY_COMMAND_FORM } from '@gantt/constants'
import {
  GanttActionOptionSelectEvent,
  GanttActionOptionSelectTarget,
  getBindTypeOptionsByStrings,
  getOptionByStr,
  getSelectOptionDisabledEvent,
  getSelectOptionDisabledTarget,
  isCellTarget,
} from '@gantt/helpers'

type useInputsParams = {
  actionTypeOptions: SelectOption[]
  eventOptions: SelectOption[]
  watchJson?: boolean
  watchEventCode?: string
  targetOptions: SelectOption[]
  methods: UseFormReturn<GanttActionType, any, undefined>
  watchActionCode?: ACTION_CODE
}

const contextMenuEvents = getBindTypeOptionsByStrings([
  EVENT_CODE_GANTT.ON_HEADER_RIGHT_CLICK,
  EVENT_CODE_GANTT.ON_ROW_RIGHT_CLICK,
])

const commandEvents = getBindTypeOptionsByStrings([
  EVENT_CODE_GANTT.ON_ROW_CLICK,
  EVENT_CODE_GANTT.ON_CELL_CLICK,
])

const tooltipEvents = getBindTypeOptionsByStrings([
  EVENT_CODE_GANTT.ON_TOOLTIP_ROW,
  EVENT_CODE_GANTT.ON_TOOLTIP_HEADER,
])

export const useInputs = ({
  actionTypeOptions,
  eventOptions,
  watchEventCode,
  targetOptions,
  methods,
  watchActionCode,
}: useInputsParams) => {
  const { modalType, currentActionResourceList, currentRow, bindingValuesResourceAction } =
    useContext(PageContext)
  const { setValue, resetField, watch } = methods
  const watchedId = watch('id')

  const handleChangeActionCode = (value: string | number) => {
    const getBind = () => {
      switch (value) {
        case ACTION_CODE.OPEN_CONTEXT_MENU:
          return GANTT_BIND_TYPE_LIST.CONTEXT_MENU
        case ACTION_CODE.EXECUTE_COMMAND:
          return GANTT_BIND_TYPE_LIST.COMMANDS
        case ACTION_CODE.OPEN_FORM_MODEL:
          return GANTT_BIND_TYPE_LIST.FORM
        case ACTION_CODE.OPEN_TOOLTIP:
          return GANTT_BIND_TYPE_LIST.TOOLTIP
        default:
          return null
      }
    }

    if (value === ACTION_CODE.EXECUTE_COMMAND) {
      setValue('actionField.commands', EMPTY_COMMAND_FORM)
    }

    const dictAction = bindingValuesResourceAction?.find(item => item.code === value)
    const title = dictAction?.title || dictAction?.code || value

    const bind = getBind()

    setValue('title', String(title))

    if (modalType === 'edit') {
      setValue('eventCode', null)
    } else {
      resetField('eventCode', { defaultValue: undefined })
    }
    setValue('actionField.bindType', bind)
  }

  const eventCodeOptions = useMemo(() => {
    if (watchActionCode === ACTION_CODE.OPEN_CONTEXT_MENU) {
      return contextMenuEvents
    }

    if (watchActionCode === ACTION_CODE.OPEN_TOOLTIP) {
      return tooltipEvents
    }

    if (watchActionCode === ACTION_CODE.EXECUTE_COMMAND) {
      return commandEvents
    }

    if (watchActionCode === ACTION_CODE.OPEN_FORM_MODEL) {
      return eventOptions
    }

    return eventOptions
  }, [watchActionCode, eventOptions])

  const actionCodeOptions = useMemo(
    () => [
      ...actionTypeOptions,
      getOptionByStr(ACTION_CODE.OPEN_CONTEXT_MENU),
      getOptionByStr(ACTION_CODE.OPEN_TOOLTIP),
      getOptionByStr(ACTION_CODE.EXECUTE_COMMAND),
    ],
    [actionTypeOptions]
  )

  const actionInputs: FormInputsType[] = [
    {
      name: 'code',
      inputType: GENERATOR_INPUT_TYPE.INPUT,
      placeholder: i18next.t('ganttCreate.actionForm.actionInputs.code.placeholder'),
      replacePattern: REGEX.MODEL_CODE_REPLACE_PATTERN,
      label: i18next.t('ganttCreate.actionForm.actionInputs.code.label'),
      disabled: modalType === 'edit',
      rules: { required: true, validate: value => value.trim().length !== 0 },
    },
    {
      name: 'actionCode',
      inputType: GENERATOR_INPUT_TYPE.SELECT,
      label: i18next.t('ganttCreate.actionForm.actionInputs.actionCode.label'),
      placeholder: i18next.t('placeholder.actionType'),
      selectOptions: actionCodeOptions,
      rules: { required: true },
      onChangeSelect: handleChangeActionCode,
      selectNoItemsMessage: i18next.t('ganttCreate.common.noItems'),
    },
    {
      inputType: GENERATOR_INPUT_TYPE.INPUT,
      label: i18next.t('ganttCreate.actionForm.actionInputs.title.label'),
      name: 'title',
      placeholder: i18next.t('ganttCreate.actionForm.actionInputs.title.placeholder'),
    },
    {
      name: 'eventCode',
      inputType: GENERATOR_INPUT_TYPE.SELECT,
      selectNoItemsMessage: i18next.t('ganttCreate.common.noItems'),
      label: i18next.t('ganttCreate.actionForm.actionInputs.eventCode.label'),
      placeholder: i18next.t('placeholder.event'),
      selectOptions: eventCodeOptions,
      renderValueSelect: value => <>{value}</>,
      rules: { required: true },
      getSelectOptionDisabled: option =>
        getSelectOptionDisabledEvent(option, currentActionResourceList, watchedId),
      renderOptionSelect: option => {
        const selectOption = option as SelectOption

        return (
          <GanttActionOptionSelectEvent
            key={selectOption.id}
            list={currentActionResourceList}
            option={selectOption}
          />
        )
      },
    },
    {
      name: 'targetRow',
      inputs: isCellTarget(watchEventCode)
        ? [
            {
              selectNoItemsMessage: i18next.t('ganttCreate.common.noItems'),
              name: 'target',
              inputType: GENERATOR_INPUT_TYPE.SELECT,
              label: i18next.t('ganttCreate.actionForm.actionInputs.column.label'),
              placeholder: i18next.t('ganttCreate.actionForm.actionInputs.column.placeholder'),
              selectOptions: targetOptions,
              renderValueSelect: value => (
                <>{typeof value === 'object' && value?.label ? value.label : value}</>
              ),
              rules: { required: true },
              getSelectOptionDisabled: option =>
                getSelectOptionDisabledTarget(
                  option,
                  currentActionResourceList,
                  watchedId,
                  watchActionCode,
                  watchEventCode
                ),
              renderOptionSelect: option => {
                const selectOption = option as SelectOption

                return (
                  <GanttActionOptionSelectTarget
                    key={selectOption.id}
                    list={currentActionResourceList}
                    option={selectOption}
                  />
                )
              },
            },
          ]
        : [],
    },
  ]

  return actionInputs
}
